import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { apiClient, getDriverApiBaseUrl } from '../lib/apiClient';

/**
 * @param {Object} options
 * @param {string} options.phone
 * @param {string} options.password
 * @param {(driverData: object) => void} [options.onLoginSuccess] — solo tras login exitoso (no en mount)
 * @param {(msg: string) => void} [options.onError]
 * @param {(msg: string) => void} [options.onSuccess]
 */
export function useDriverAuth({ phone, password, onLoginSuccess, onError, onSuccess }) {
  const [loggedIn, setLoggedIn] = useState(false);
  const [driver, setDriver] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const loggedInRef = useRef(false);
  loggedInRef.current = loggedIn;

  const onLoginSuccessRef = useRef(onLoginSuccess);
  onLoginSuccessRef.current = onLoginSuccess;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const fetchDriverData = useCallback(async (opts = {}) => {
    const authToken = localStorage.getItem('driver_token');
    if (!authToken) {
      console.error('❌ No hay token de autenticación');
      setLoggedIn(false);
      throw new Error('No hay token de autenticación');
    }

    try {
      console.log('🔍 Obteniendo información del repartidor...');
      const response = await apiClient.get('/drivers/');

      let driverList = [];
      if (Array.isArray(response.data)) {
        driverList = response.data;
      } else if (response.data && response.data.results) {
        driverList = response.data.results;
      } else if (response.data && response.data.data) {
        driverList = response.data.data;
      }

      if (driverList && driverList.length > 0) {
        const driverData = driverList[0];
        console.log('✅ Repartidor encontrado:', driverData);
        setDriver(driverData);
        if (!loggedInRef.current) {
          setLoggedIn(true);
          setToken(authToken);
        }
        if (opts.notifyLoginSuccess) {
          onLoginSuccessRef.current?.(driverData);
        }
        return driverData;
      }

      console.error('❌ No se encontraron repartidores en la respuesta');
      if (loggedInRef.current) {
        onErrorRef.current?.(
          'No se encontró información del repartidor. Esto puede deberse a que:\n\n' +
            '1. Tu cuenta no tiene un perfil de repartidor asociado\n' +
            '2. El token de sesión ha expirado\n\n' +
            'Por favor, cierra sesión e inicia sesión nuevamente.'
        );
      }
      setLoggedIn(false);
      setToken(null);
      localStorage.removeItem('driver_token');
      localStorage.removeItem('driver_refresh_token');
      throw new Error('No se encontró información del repartidor');
    } catch (error) {
      console.error('❌ Error fetching driver data:', error);
      if (loggedInRef.current) {
        if (error.response?.status === 401) {
          onErrorRef.current?.('Sesión expirada o token inválido. Por favor, inicia sesión nuevamente.');
        } else if (error.response?.status === 403) {
          onErrorRef.current?.(
            'No tienes permisos para acceder a esta información. Verifica que tu cuenta tenga rol de repartidor.'
          );
        } else {
          const errorMsg = error.response?.data?.error ||
            error.response?.data?.detail ||
            error.message ||
            'Error desconocido';
          onErrorRef.current?.(
            `Error al cargar información del repartidor: ${errorMsg}\n\nPor favor, intenta iniciar sesión nuevamente.`
          );
        }
      }
      setLoggedIn(false);
      setToken(null);
      localStorage.removeItem('driver_token');
      localStorage.removeItem('driver_refresh_token');
      throw error;
    }
  }, []);

  useEffect(() => {
    const onDriverLogout = () => {
      setLoggedIn(false);
      setToken(null);
      setDriver(null);
      localStorage.removeItem('driver_token');
      localStorage.removeItem('driver_refresh_token');
    };
    window.addEventListener('driver_logout', onDriverLogout);
    return () => window.removeEventListener('driver_logout', onDriverLogout);
  }, []);

  useEffect(() => {
    const savedToken = localStorage.getItem('driver_token');
    if (savedToken) {
      console.log('🔑 Token encontrado en localStorage, verificando validez...');
      setLoading(true);
      fetchDriverData()
        .then(() => {
          setToken(savedToken);
          setLoggedIn(true);
          setLoading(false);
        })
        .catch(() => {
          console.log('⚠️ Token inválido o expirado, limpiando...');
          localStorage.removeItem('driver_token');
          localStorage.removeItem('driver_refresh_token');
          setLoggedIn(false);
          setToken(null);
          setLoading(false);
        });
    } else {
      console.log('ℹ️ No hay token guardado, mostrando pantalla de login');
      setLoading(false);
    }
  }, [fetchDriverData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${getDriverApiBaseUrl()}/auth/login/`, {
        phone,
        password,
      });
      const { access, refresh, user } = response.data;
      if (user.role !== 'driver') {
        onErrorRef.current?.('Esta app es solo para repartidores');
        return;
      }
      setToken(access);
      setLoggedIn(true);
      localStorage.setItem('driver_token', access);
      if (refresh) {
        localStorage.setItem('driver_refresh_token', refresh);
      }
      await fetchDriverData({ notifyLoginSuccess: true });
      onSuccessRef.current?.('Inicio de sesión correcto');
    } catch (error) {
      onErrorRef.current?.('Error al iniciar sesión. Verifica tus credenciales.');
    }
  };

  const handleLogout = () => {
    setLoggedIn(false);
    setToken(null);
    setDriver(null);
    localStorage.removeItem('driver_token');
    localStorage.removeItem('driver_refresh_token');
    window.dispatchEvent(new Event('driver_logout'));
  };

  return {
    loggedIn,
    driver,
    token,
    loading,
    handleLogin,
    handleLogout,
    fetchDriverData,
    setDriver,
    setLoggedIn,
    setToken,
  };
}
